/**
 * React hooks for live GA4 data fetching.
 *
 * Each hook reads the active date range from monarchStore and
 * re-fetches whenever it changes. Components just call the hook
 * and get { data, loading, error, refetch }.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import useMonarchStore from '@/stores/monarchStore';
import {
  fetchOverview,
  fetchChannels,
  fetchPages,
  fetchEvents,
  fetchFunnel,
  fetchScrollDepth,
  fetchTopClicks,
  fetchRealtime,
} from '@/services/api';
import type {
  GA4OverviewData,
  GA4ChannelData,
  GA4PageData,
  GA4EventData,
  GA4FunnelStage,
  GA4ScrollDepthEntry,
  GA4ClickData,
  GA4RealtimeData,
} from '@/types/dashboard';

/* ============================================================================
   Generic result type
   ============================================================================ */

export interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/* ============================================================================
   Generic hook — reads date range from store, fetches on change
   ============================================================================ */

function useApiData<T>(
  fetcher: (start: Date, end: Date) => Promise<T>,
): UseApiResult<T> {
  const { start, end } = useMonarchStore((s) => s.ui.dateRange);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher(start, end);
      if (mountedRef.current) {
        setData(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [fetcher, start, end]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { data, loading, error, refetch: doFetch };
}

/* ============================================================================
   Typed hooks — one per endpoint
   ============================================================================ */

export function useOverview(): UseApiResult<GA4OverviewData> {
  return useApiData(fetchOverview);
}

export function useChannels(): UseApiResult<GA4ChannelData[]> {
  return useApiData(fetchChannels);
}

export function usePages(): UseApiResult<GA4PageData[]> {
  return useApiData(fetchPages);
}

export function useEvents(): UseApiResult<GA4EventData[]> {
  return useApiData(fetchEvents);
}

export function useFunnel(): UseApiResult<GA4FunnelStage[]> {
  return useApiData(fetchFunnel);
}

/* Scroll depth returns extra metadata (source, note) */

export interface UseScrollDepthResult {
  data: GA4ScrollDepthEntry[] | null;
  source: string | undefined;
  note: string | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useScrollDepth(): UseScrollDepthResult {
  const { start, end } = useMonarchStore((s) => s.ui.dateRange);
  const [data, setData] = useState<GA4ScrollDepthEntry[] | null>(null);
  const [source, setSource] = useState<string | undefined>();
  const [note, setNote] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchScrollDepth(start, end);
      if (mountedRef.current) {
        setData(result.data);
        setSource(result.source);
        setNote(result.note);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [start, end]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { data, source, note, loading, error, refetch: doFetch };
}

export function useTopClicks(): UseApiResult<GA4ClickData[]> {
  return useApiData(fetchTopClicks);
}

/* Realtime — polls every 30 seconds, no date range dependency */

export function useRealtime(): UseApiResult<GA4RealtimeData> {
  const [data, setData] = useState<GA4RealtimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchRealtime();
      if (mountedRef.current) {
        setData(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    doFetch();
    const interval = setInterval(doFetch, 30_000);
    return () => clearInterval(interval);
  }, [doFetch]);

  return { data, loading, error, refetch: doFetch };
}
